/**
 * Fetches the rolling 30-day per-resolver p50 snapshot from /api/history.
 * One fetch per page load, cached in a module-level promise so multiple
 * components asking for it (e.g., each sparkline row) share the same request.
 */

export type Snapshot = {
  updatedAt: string;
  days: string[];                 // oldest -> newest, up to 30 entries
  resolvers: Record<string, (number | null)[]>;
};

let cached: Promise<Snapshot> | null = null;

export function fetchHistory(): Promise<Snapshot> {
  if (cached) return cached;
  cached = fetch("/api/history", { cache: "no-store" })
    .then((r) => {
      if (!r.ok) throw new Error(`history ${r.status}`);
      return r.json() as Promise<Snapshot>;
    })
    .catch((err) => {
      // Reset so a transient failure doesn't permanently lock us out.
      cached = null;
      throw err;
    });
  return cached;
}

/** Compute typical-day latency across all resolvers' most recent day (median of medians). */
export function typicalLatencyMs(snapshot: Snapshot): number | null {
  const latest = snapshot.days.length - 1;
  if (latest < 0) return null;
  const vals: number[] = [];
  for (const arr of Object.values(snapshot.resolvers)) {
    const v = arr[latest];
    if (typeof v === "number" && Number.isFinite(v)) vals.push(v);
  }
  if (vals.length === 0) return null;
  const sorted = vals.slice().sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 === 0
    ? Math.round((sorted[mid - 1] + sorted[mid]) / 2)
    : sorted[mid];
}
